import { cpOrdersListingApi } from '@apis/cp/orders.js';
import { Icon, Modal, PaymentMethod, Status, Table, TextCopy, TextWithIcon, Toast } from '@common-web/index.js';
import { PLATFORM_ICONS, pageSizeOptions } from '@constants/generic-constants.js';
import { strings } from '@constants/strings.js';
import { useGetRequest } from '@hooks/index.js';
import { formatCurrency, formatPhoneNo, getErrorString, getFormattedDate } from '@utils/utils.js';
import tcsLogo from 'assets/icons/tcsLogo.png';
import leopardsLogo from 'assets/icons/leopardsLogo.png';

import { Flex, Pagination, Tooltip } from 'antd';
import { useState } from 'react';
import { useSelector } from 'react-redux';

const OrderListingModal = ({ visible, onClose, filters, title, count = 1 }) => {
  const { currency } = useSelector((state) => state?.tenantData?.tenant ?? {});
  const [pageSize, setPageSize] = useState(pageSizeOptions?.[0]);
  const [currentPage, setCurrentPage] = useState(1);

  const buildQueryParams = (page = 1, size = pageSize) => {
    return `?${filters}&page=${page}&limit=${pageSize}`;
  };

  const { data: ordersResponse, loading } = useGetRequest({
    key: ['order-listing-modal', filters, currentPage, pageSize],
    fetch: async () => {
      try {
        const params = buildQueryParams(currentPage, pageSize);
        const response = await cpOrdersListingApi(params);
        if (response?.success) {
          return response;
        }
        return { orders: [], pagination: null };
      } catch (error) {
        Toast.error(getErrorString(error));
        return { orders: [], pagination: null };
      }
    },
    enabled: visible && !!filters,
    noCache: true,
  });

  const ordersData = ordersResponse?.orders || [];
  const pagination = ordersResponse?.pagination || null;

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const getTableColumns = () => [
    {
      title: strings.store_details,
      dataIndex: 'store_details',
      key: 'store_details',
      width: 200,
    },
    {
      title: strings.order_id,
      dataIndex: 'order_id',
      key: 'order_id',
      width: 150,
    },
    {
      title: strings.sub_order_id,
      dataIndex: 'sub_order_id',
      key: 'sub_order_id',
      width: 150,
    },
    {
      title: strings.order_price,
      dataIndex: 'order_price',
      key: 'order_price',
      width: 200,
    },
    {
      title: strings.buyer,
      dataIndex: 'buyer',
      key: 'buyer',
      width: 250,
    },
    {
      title: strings.status,
      dataIndex: 'status',
      key: 'status',
      width: 150,
    },
  ];

  const formatOrdersData = () => {
    if (!ordersData?.length) {
      return [];
    }

    return ordersData.map((order) => {
      const {
        id,
        line_items_count,
        net_amount,
        shipping_charges,
        placed_at,
        status,
        disposition,
        shipping_address,
        payment_method,
        store,
        invoice = {},
        platform,
        fee,
        adjusted_amount,
        courier_service,
        consignment,
      } = order;

      const combinedAddressTitle = [
        shipping_address?.house_number,
        shipping_address?.address,
        shipping_address?.landmark,
        shipping_address?.area?.title,
        shipping_address?.location?.title,
      ]
        .filter(Boolean)
        .join(', ');

      return {
        ID: id,
        key: id,
        store_details: (
          <Flex vertical gap={6}>
            <span className="fw-600">{store?.name}</span>
            <div className="fs-12">
              <span className="gray-500">{strings?.store_id}:</span>{' '}
              <span style={{ color: 'var(--ant-color-primary)' }}>{store?.id}</span>
            </div>
            <Flex align="center" gap={4}>
              <Icon icon="IoLocationSharp" color="var(--gray-300" iconSize={16} />
              <span className="fw-700">{store?.city?.title || '-'}</span>
            </Flex>
          </Flex>
        ),
        sub_order_id: <TextCopy value={id} />,
        order_id: <TextCopy value={invoice?.id} />,
        order_price: (
          <Flex vertical gap={6}>
            <Flex align={'center'} gap={8}>
              <span className="fw-700">{formatCurrency({ currency, price: net_amount })}</span>
              <span style={{ color: 'var(--gray-300' }}>|</span>
              <Flex gap={4} align={'center'}>
                <p className={'m-0'}>{line_items_count || '-'}</p>
              </Flex>
              <span style={{ color: 'var(--gray-300' }}>|</span>
              {courier_service?.display_name && (
                <Flex align="center" gap={8}>
                  {courier_service.name === 'tcs' && (
                    <img src={tcsLogo} alt="tcs" style={{ height: '16px', objectFit: 'contain' }} />
                  )}
                  {courier_service.name === 'leopard' && (
                    <img src={leopardsLogo} alt="tcs" style={{
                      height: '20px',
                      objectFit: 'contain',
                      backgroundColor: '#ffcb03',
                      padding: '2px',
                      borderRadius: '3px'
                    }} />
                  )}
                </Flex>
              )}
            </Flex>
            <Flex gap={12} align="center" wrap>
              <TextWithIcon
                iconColor={'var(--gray-300)'}
                iconSize={16}
                icon="NetAmountIcon"
                text={<span>{(adjusted_amount || net_amount)?.toLocaleString()}</span>}
              />
              <TextWithIcon
                iconColor={'var(--gray-300)'}
                iconSize={16}
                icon="IconShipment"
                text={<span>{shipping_charges?.toLocaleString()}</span>}
              />
              {!!fee && (
                <TextWithIcon
                  iconColor={'var(--gray-300)'}
                  iconSize={16}
                  icon="FeePrecentIcon"
                  text={<span>{Math.round(fee)?.toLocaleString()}</span>}
                />
              )}
            </Flex>
            <PaymentMethod iconSize={14} iconColor={'var(--gray-300)'} slug={payment_method} />
            <Tooltip title={`Placed on: ${getFormattedDate(placed_at)}`} placement={'topLeft'}>
              <TextWithIcon
                iconColor={'var(--gray-300)'}
                iconSize={17}
                icon="IoTimeSharp"
                text={getFormattedDate(placed_at)}
              />
            </Tooltip>
            {courier_service?.display_name && (
              <Flex align="center" gap={8}>
                {!!consignment?.tracking_number && (
                  <TextCopy
                    value={consignment?.tracking_number}
                    style={{ fontSize: 'var(--ant-font-size-sm)' }}
                    removeSpaces={true}
                    showToolTip={false}
                  />
                )}
              </Flex>
            )}
          </Flex>
        ),
        buyer: (
          <Flex vertical gap={4}>
            <span className="fw-700 ml-20">{shipping_address?.name}</span>
            {shipping_address?.mobile && (
              <TextWithIcon
                iconSize={16}
                iconColor={'var(--gray-300)'}
                icon={'MdPhone'}
                component={
                  <TextCopy
                    value={formatPhoneNo(shipping_address?.mobile)}
                    style={{ fontSize: 'var(--ant-font-size-sm)' }}
                    removeSpaces={true}
                  />
                }
              />
            )}
            {combinedAddressTitle && (
              <TextWithIcon
                icon={'BiSolidBuildingHouse'}
                iconSize={16}
                iconColor={'var(--gray-300)'}
                component={
                  <Tooltip title={combinedAddressTitle} placement="topLeft" overlayStyle={{ maxWidth: 400 }}>
                    <span className={'text-ellipsis'} style={{ maxWidth: 300 }}>
                      {combinedAddressTitle}
                    </span>
                  </Tooltip>
                }
              />
            )}
            <Flex align="center" gap={12}>
              {shipping_address?.location?.title && (
                <TextWithIcon
                  iconSize={16}
                  iconColor={'var(--gray-300)'}
                  icon={'IoLocationSharp'}
                  component={<span className={'fw-700'}>{shipping_address.location.title}</span>}
                />
              )}
              <Flex gap={4} align={'center'}>
                {PLATFORM_ICONS[platform]}
                <span>{platform ? platform : '-'}</span>
              </Flex>
            </Flex>
          </Flex>
        ),
        status: status ? (
          <Flex gap={8} vertical align="start">
            <Status
              color={status?.background_hex?.seller_center}
              textColor={status?.colour_hex?.seller_center}
              name={disposition?.name || status?.name}
            />
          </Flex>
        ) : (
          '-'
        ),
      };
    });
  };

  return (
    <Modal
      open={visible}
      title={title}
      handleCancel={onClose}
      showActions={false}
      width="85vw"
      bodyStyle={{
        maxHeight: 700,
      }}
      destroyOnClose
      ModalBody={
        <>
          <Table
            columns={getTableColumns()}
            dataSource={formatOrdersData()}
            loading={loading}
            loaderRowCount={count < 10 ? count : 10}
          />
          <Flex justify="flex-end">
            <Pagination
              rootClassName={'mt-8'}
              total={pagination?.total_count}
              current={currentPage}
              pageSize={pageSize}
              onShowSizeChange={(current, size) => handlePageChange(1, size)}
              onChange={(page, newPageSize) => handlePageChange(page, newPageSize)}
              pageSizeOptions={pageSizeOptions}
              align={'end'}
            />
          </Flex>
        </>
      }
    />
  );
};

export default OrderListingModal;
